import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import React, { useState, useEffect } from 'react';

/**
 * Formula Configuration Modal
 * Dedicated popup for configuring custom widget formulas
 */
const FormulaConfigModal = ({ isOpen, onClose, onSave, initialFormula = '', theme = 'light' }) => {
  const [formula, setFormula] = useState(initialFormula);

  useEffect(() => {
    if (isOpen) {
      setFormula(initialFormula);
    }
  }, [isOpen, initialFormula]);

  const handleSave = () => {
    onSave(formula);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalSurface closeOnEscape={false} onClose={onClose} label="Custom formula" overlayClassName="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4" panelClassName="rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-[var(--surface-raised)]" panelProps={{"data-m6-layout":"widgets","data-m6-component":"FormulaConfigModal"}}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--surface-border)] bg-[var(--surface-sunken)]">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2 text-[var(--ink)]">
              🧮 Custom Formula Builder
            </h3>
            <p className="text-sm mt-1 text-[var(--ink-muted)]">
              Create custom calculations using variables and operators
            </p>
          </div>
          <button
            onClick={onClose}
            className="ia-modal-desktop-close p-2 rounded-lg transition-colors text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-sunken)]"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Formula Input */}
          <div>
            <label className="block text-sm font-semibold mb-3 text-[var(--ink)]">
              Formula Expression
            </label>
            <textarea
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              placeholder="Example: (revenue - expenses) / revenue * 100"
              rows={6}
              className="w-full px-4 py-3 text-base font-mono rounded-lg border-2 transition-all resize-none bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ui-positive)] placeholder-[var(--ink-muted)] focus:border-[var(--accent-ink)] focus:outline-none focus:ring-4 focus:ring-[var(--accent-ink)]/20"
            />
          </div>

          {/* Available Variables Section */}
          <div className="p-4 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)]">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-[var(--ink)]">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Available Variables
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                { var: 'revenue', desc: 'Total revenue amount', icon: '💰' },
                { var: 'expenses', desc: 'Total expenses', icon: '💸' },
                { var: 'profit', desc: 'Net profit (revenue - expenses)', icon: '📈' },
                { var: 'clients', desc: 'Number of clients', icon: '👥' },
                { var: 'invoices', desc: 'Total invoices count', icon: '📄' },
                { var: 'payments', desc: 'Completed payments', icon: '✅' },
              ].map(item => (
                <button
                  key={item.var}
                  onClick={() => setFormula(prev => prev + (prev ? ' + ' : '') + item.var)}
                  className="text-left p-3 rounded-lg transition-all bg-[var(--surface-raised)] hover:bg-[var(--surface-sunken)] border border-[var(--surface-border)]"
                >
                  <div className="text-sm font-mono font-semibold flex items-center gap-2 text-[var(--ui-positive)]">
                    <span>{item.icon}</span>
                    {item.var}
                  </div>
                  <div className="text-xs mt-1 text-[var(--ink-muted)]">
                    {item.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Operators Section */}
          <div className="p-4 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)]">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-[var(--ink)]">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              Operators &amp; Functions
            </h4>
            <div className="grid grid-cols-4 gap-2">
              {['+', '-', '*', '/', '(', ')', '%', '^'].map(op => (
                <button
                  key={op}
                  onClick={() => setFormula(prev => prev + ' ' + op + ' ')}
                  className="p-2 text-center font-mono font-bold text-lg rounded-lg transition-all bg-[var(--surface-raised)] hover:bg-[var(--surface-sunken)] text-[var(--accent-ink)] border border-[var(--surface-border)]"
                >
                  {op}
                </button>
              ))}
            </div>
          </div>

          {/* Examples Section */}
          <div className="p-4 rounded-lg border border-[var(--nav-active-edge)]/30 bg-[var(--nav-active-bg)]">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-[var(--nav-active-ink)]">
              💡 Formula Examples
            </h4>
            <div className="space-y-2">
              {[
                { label: 'Profit Margin %', formula: '(revenue - expenses) / revenue * 100' },
                { label: 'Revenue Per Client', formula: 'revenue / clients' },
                { label: 'Net Profit', formula: 'revenue - expenses' },
                { label: 'Payment Completion Rate', formula: 'payments / invoices * 100' },
              ].map(example => (
                <button
                  key={example.label}
                  onClick={() => setFormula(example.formula)}
                  className="w-full text-left p-3 rounded-lg transition-all bg-[var(--surface-raised)] hover:bg-[var(--surface-sunken)] border border-[var(--surface-border)]"
                >
                  <div className="text-sm font-medium text-[var(--ink)]">
                    {example.label}
                  </div>
                  <div className="text-xs font-mono mt-1 text-[var(--accent-ink)]">
                    {example.formula}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[var(--surface-border)] bg-[var(--surface-sunken)]">
          <button
            onClick={() => setFormula('')}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-border)]"
          >
            Clear Formula
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium rounded-lg border transition-colors text-[var(--ink)] bg-[var(--surface-raised)] border-[var(--surface-border)] hover:bg-[var(--surface-sunken)]"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2.5 text-sm font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] transition-all shadow-lg"
            >
              Save Formula
            </button>
          </div>
        </div>
      </ModalSurface>
  );
};

export default FormulaConfigModal;
